import { assertNotEmpty } from '@festina-life/core'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  type SubTemplateFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import { TemplatePathParts } from '../../uiModels/templateOperations/helpers'
import { MergeableSubTemplateFieldValueProps } from '../templateFieldsTable/fieldsHelpers'

type RequiredSubTemplatePathsAndValues = {
  path: string
  isIdentifiable: boolean
}

export const useCollectInexistentSubtemplatePaths = (
  forPath: string,
): RequiredSubTemplatePathsAndValues[] => {
  const { fieldValueForPath, templateVersion } = useTemplateContext()
  assertNotEmpty(templateVersion)

  return useMemo(() => {
    const groups = TemplatePathParts.exec(forPath)?.groups

    if (!groups) {
      return []
    }

    const { intermediatePath } = groups
    const pathParts = intermediatePath.split('.')

    // oxlint-disable-next-line max-params
    return pathParts.reduce((failedPaths, part, currentIndex) => {
      const isSubTemplatedVariant = !!/^\w+\{\w+\}$/.exec(part)?.shift()
      const fieldPath = `{${templateVersion.versionInfo.identifier}}.${pathParts.slice(0, currentIndex + 1).join('.')}`

      if (isSubTemplatedVariant) {
        const unflattenedFieldValue = fieldValueForPath<SubTemplateFieldValue>(
          fieldPath,
          'unflattened',
        )

        const isFieldValueEmpty =
          !unflattenedFieldValue ||
          (!unflattenedFieldValue?.fields.some((field) => !!field.value) &&
            !unflattenedFieldValue.dynamicFunctions?.length &&
            !MergeableSubTemplateFieldValueProps.some(
              (valueProp) => unflattenedFieldValue[valueProp],
            ))

        const fieldValue = fieldValueForPath<SubTemplateFieldValue>(
          fieldPath,
          'current',
        )

        if (isFieldValueEmpty) {
          assertNotEmpty(fieldValue)

          failedPaths.push({
            path: fieldPath,
            isIdentifiable:
              fieldValue.subTemplatedField?.role ===
              ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE,
          })
        }
      }

      return failedPaths
    }, [] as RequiredSubTemplatePathsAndValues[])
  }, [fieldValueForPath, forPath, templateVersion.versionInfo.identifier])
}
